/**
 * The press path for a verb, and the one door out of the Up next line.
 *
 * Every press of the six moves, the check (the survey, on the seed), the panel
 * and the conjecture line's three — Propose conjectures, Search for prior art and
 * Find what’s unsettled — enters `pressVerb`, from every door that carries one: a
 * key, the node card's verbs, its check, survey, panel and conjecture buttons, a
 * palette row, a proposal's chip (`actPress.ts`) and the coach's Take
 * (`Coach.tsx`). One path, so a press
 * cannot be taken by one door and refused by another, and the drain's
 * re-judgement and the durable-save wait below cover all of them without a
 * second queue path. With the lock free it is the press it always was: the verb
 * runs now, and the verb's own guards say why when it cannot. With the lock
 * held it is no longer dropped — it is queued behind the pass in flight
 * (`core/upNext.ts` has the rules and the guardrail) or refused out loud, in the
 * sentence a button under the lock has always used. A door that must decide
 * before it commits to anything else — the chip selects its node, the coach
 * retires its cue — asks `pressRefusal` first, which is the question
 * `pressVerb` itself asks.
 *
 * Two presses carry more than a verb onto the line, and have a door each: the
 * rewrite editor's "Re-derive from here" (`pressRewrite`, with the reader's
 * draft and the count its face printed) and "Rethink “<branch>”"
 * (`pressRethink`, with the branch it named). Past the door they are the same
 * press — one path, one line, one drain — so they meet the same re-judgement,
 * and their own terms on top of it (`core/upNext.ts`).
 *
 * The drain is armed from App's boot, like the narrator, because it SPENDS: a
 * subscription that began the moment anything imported a helper from here
 * would be an authorisation nobody can point to. It runs when the lock falls,
 * awaits the durable save before it looks at anything (D84: a memory write is
 * pending work until that boundary commits), re-judges the first waiting item
 * against the store as it is NOW, and dispatches through the same verbs a press
 * reaches — whose transport awaits that boundary again before any paid call.
 * It never goes around them.
 *
 * WHY `ui/` AND NOT `engine/`. It is `promotePress.ts`'s kind of module: a
 * plain-`.ts` dispatcher beside the components, testable without a renderer,
 * and the toasts it raises are the press's own words.
 */

import { beforePaidCall } from '../core/durability'
import { uid } from '../core/id'
import { fmtUsd } from '../core/models'
import { onRenamed } from '../core/rename'
import { rewriteLoss, type RewriteLoss } from '../core/rewrite'
import { resolveFor, useStore } from '../core/store'
import {
  nextRethink,
  upNextCalled,
  upNextGone,
  upNextName,
  upNextPriceVerb,
  upNextRefusal,
  upNextRole,
  upNextVerdict,
  upNextWaits,
  type UpNextItem,
  type UpNextKind,
  type UpNextVerb,
  type Wording,
} from '../core/upNext'
import {
  rederiveFromVerification,
  reviseNode,
  runConjecture,
  runOp,
  runPanel,
  runPriorArt,
  runSweep,
  runVerify,
} from '../engine/reason'
import { projectVerbUsd } from '../engine/run'

type StoreState = ReturnType<typeof useStore.getState>

const estimateOf = (s: StoreState, verb: UpNextKind): number | null =>
  projectVerbUsd({ modelId: s.modelId, roleModels: s.roleModels }, upNextPriceVerb(verb))

/** What one press carries: its verb, and for the rewrite and the rethink the
 *  terms their buttons showed. An item on the line is one of these, plus where
 *  and when it was pressed. */
type Pressed =
  | { verb: UpNextVerb }
  | { verb: 'revise'; draft: Wording; loss: RewriteLoss }
  | { verb: 'rebase'; branch: { id: string; label: string } }

/** The verb itself — the same call the key and the button made before a queue
 *  existed, so a deferred press and an immediate one cannot learn two paths. */
function fire(press: Pressed, nodeId: string): void {
  switch (press.verb) {
    case 'verify':
      return void runVerify(nodeId)
    case 'panel':
      return void runPanel(nodeId)
    // The conjecture line's three, each through its own pass — never `runOp`,
    // whose schema and brief are a move's (D286).
    case 'conjecture':
      return void runConjecture(nodeId)
    case 'priorArt':
      return void runPriorArt(nodeId)
    case 'sweep':
      return void runSweep(nodeId)
    // The draft is the whole of what the reader wrote; `reviseNode` counts what
    // it replaces again for its own toast, from the same walk the press read.
    case 'revise':
      return void reviseNode(nodeId, press.draft)
    // Takes the queue's next one-pass branch, which is the one the press named:
    // with the lock free it is the button's own render, and from the line the
    // drain has just checked it still is.
    case 'rebase':
      return void rederiveFromVerification(nodeId)
    default:
      return void runOp(press.verb, nodeId)
  }
}

export type PressOutcome = 'ran' | 'queued' | 'refused'

/** What the lock's answer reads: the line, the lock, and whether the verb's
 *  role has a key. Narrow, so a door can ask it off the fields it holds. */
export type PressView = Pick<
  StoreState,
  | 'busy'
  | 'interrupt'
  | 'run'
  | 'session'
  | 'upNext'
  | 'modelId'
  | 'roleModels'
  | 'keys'
  | 'customBase'
  | 'customModel'
>

/**
 * Why a press of this verb made now would be refused BY THE LOCK, in the
 * sentence the reader gets — or null when it would be taken, to run now or to
 * wait on the line.
 *
 * Only the lock's half. With the lock free the verb runs and its own guards
 * speak (a missing key, the seed's panel), exactly as a key's press always has;
 * this answers the one question a press meets only while a pass is in flight.
 * A door that prints its refusal before the press (a proposal's chip) or must
 * not commit to anything on a refusal (the coach retires its cue) asks this,
 * and `pressVerb` asks it too, so what the door showed and what the press does
 * are one reading.
 */
export function pressRefusal(s: PressView, verb: UpNextKind, nodeId: string): string | null {
  if (!upNextWaits(s, verb)) return null
  return upNextRefusal(s, verb, nodeId, resolveFor(s, upNextRole(verb)) !== null)
}

/**
 * One press of a verb on a node.
 *
 * `keyed` is the keyboard's price parity: a button shows its figure on hover
 * before the click, a key has no hover, so a keyed paid press says the same
 * `projectVerbUsd` figure as it fires — or as it is queued.
 */
export function pressVerb(
  verb: UpNextVerb,
  nodeId: string,
  opts: { keyed?: boolean } = {},
): PressOutcome {
  return press({ verb }, nodeId, opts)
}

/**
 * The rewrite editor's priced press: re-derive from the reader's words.
 *
 * `loss` is what the button's face said it replaces, passed from the render the
 * reader pressed rather than read again here: under the lock a pass may be
 * landing claims beneath this one, and the promise is the count that was on
 * screen. Taken — run now or up next — the editor closes; refused, it stays
 * open with the draft in it, because a refusal must not cost the reader what
 * they wrote.
 */
export function pressRewrite(nodeId: string, draft: Wording, loss: RewriteLoss): PressOutcome {
  return press({ verb: 'revise', draft: { label: draft.label, claim: draft.claim }, loss }, nodeId)
}

/** "Rethink “<branch>”": the branch is the one its button named. */
export function pressRethink(nodeId: string, branch: { id: string; label: string }): PressOutcome {
  return press({ verb: 'rebase', branch: { id: branch.id, label: branch.label } }, nodeId)
}

function press(pressed: Pressed, nodeId: string, opts: { keyed?: boolean } = {}): PressOutcome {
  const s = useStore.getState()
  const { verb } = pressed
  const name = upNextName(verb, s.session.nodes[nodeId]?.parentId === null)
  if (!upNextWaits(s, verb)) {
    // Only when the pass can actually run: pricing a keyless refusal would put
    // a figure on nothing. The verb's route, never the selection's key — a
    // routed role with no key refuses, and its figure would be for nothing too.
    if (opts.keyed && resolveFor(s, upNextRole(verb)) !== null) {
      const est = estimateOf(s, verb)
      if (est !== null)
        s.toast(`${name} — up to about ${fmtUsd(est)}, an estimate that leans high.`, 'info')
    }
    // A "Run it?" chip for this same press is answered by this one. Left on the
    // line it would still offer the pass, and pressing it later would run it a
    // second time for one intent — the one thing D162 says a press never does.
    const answered = s.upNext.filter((i) => i.held && i.verb === verb && i.nodeId === nodeId)
    if (verb !== 'revise') {
      for (const i of answered) s.unqueuePress(i.id)
      fire(pressed, nodeId)
      return 'ran'
    }
    // …except that a rewrite's chip holds the reader's draft, so it goes only
    // once this press has actually begun its pass. `reviseNode` refuses before
    // it destroys anything (no key, the lock), and a refused press must leave
    // the draft where the reader can press it again.
    fire(pressed, nodeId)
    if (!useStore.getState().busy) return 'refused'
    for (const i of answered) useStore.getState().unqueuePress(i.id)
    return 'ran'
  }
  const refusal = pressRefusal(s, verb, nodeId)
  if (refusal) {
    s.toast(refusal, 'warn')
    return 'refused'
  }
  const item = queued(s, pressed, nodeId)
  if (typeof item === 'string') {
    s.toast(item, 'warn')
    return 'refused'
  }
  s.queuePress(item)
  const called = upNextCalled(item) ?? name
  // The line under the live pass is the lasting answer to a press; this is the
  // one announcement of it — for a screen reader, and for a column showing
  // another tab. A key's carries the figure, as a key that fires does, because
  // a key has no hover to have shown it first.
  s.toast(
    opts.keyed && item.est !== null
      ? `${called} is up next — up to about ${fmtUsd(item.est)}, an estimate that leans high.`
      : `${called} is up next.`,
    'info',
  )
  return 'queued'
}

/**
 * The line's item for a press the lock refused only for being second — or the
 * sentence saying why it cannot be one. Everything the drain will hold it to is
 * read here, at the press: the estimate, and a carried press's own terms. The
 * rewrite's are the words it replaces as they stand now (a rewrite landing first
 * changes them); the rethink's, the check it read.
 */
function queued(s: StoreState, pressed: Pressed, nodeId: string): UpNextItem | string {
  const node = s.session.nodes[nodeId]!
  const base = {
    id: uid('next'),
    nodeId,
    label: node.label,
    sessionId: s.session.id,
    est: estimateOf(s, pressed.verb),
  }
  if (pressed.verb === 'revise')
    return { ...base, ...pressed, wording: { label: node.label, claim: node.claim } }
  if (pressed.verb === 'rebase') {
    // `rederiveFromVerification` returns without a word on a claim with no
    // check; a press that waits must not wait for that.
    if (!node.verification) return `There is no check on “${node.label}” to rethink from.`
    return { ...base, ...pressed, checkedAt: node.verification.checkedAt }
  }
  return { ...base, verb: pressed.verb }
}

/** The ✕: one press off the line, nothing else touched. */
export function dropUpNext(id: string): void {
  useStore.getState().unqueuePress(id)
}

/**
 * A chip that asks again, answered. The new press is a press like any other —
 * it runs now if the lock is free, and joins the line again if not — at the
 * figure the chip shows, which is today's, and on the terms it shows: a
 * rewrite's chip counts what it would replace now, and a rethink's names the
 * branch it would take now (`UpNextLine` reads both live), so those are what
 * its press is held to.
 *
 * The chip stays until the press that answers it is taken. `press` takes it off
 * the line when that press runs, and `queuePress` replaces it when that press
 * waits; a press refused leaves it where it was — and a rewrite's draft with it.
 */
export function takeUpNext(id: string): PressOutcome | null {
  const s = useStore.getState()
  const item = s.upNext.find((i) => i.id === id)
  if (!item) return null
  // A verb pressed on a node that is gone does nothing at all, which is right
  // for a key (the selection went with it) and wrong for a chip naming it.
  if (!s.session.nodes[item.nodeId]) {
    s.unqueuePress(id)
    s.toast(upNextGone(item), 'warn')
    return 'refused'
  }
  if (item.verb === 'revise')
    return pressRewrite(item.nodeId, item.draft, rewriteLoss(s.session, item.nodeId))
  if (item.verb === 'rebase')
    return pressRethink(item.nodeId, nextRethink(s.session, item.nodeId) ?? item.branch)
  return pressVerb(item.verb, item.nodeId)
}

let draining = false
let again = false

/** How many times the drain asks the durable boundary before it hands the
 *  presses back — see the refusal in `drainOnce`. */
const DURABLE_ASKS = 3

/**
 * Run what is up next, if its turn has come. Idempotent and re-entrant-safe:
 * a lock that falls while this is awaiting the save asks for one more look
 * rather than a second drain beside the first.
 */
export async function drainUpNext(): Promise<void> {
  if (draining) {
    again = true
    return
  }
  draining = true
  try {
    do {
      again = false
      await drainOnce()
    } while (again)
  } finally {
    draining = false
  }
}

async function drainOnce(): Promise<void> {
  const waiting = () => {
    const s = useStore.getState()
    return s.upNext.filter((i) => !i.held && i.sessionId === s.session.id)
  }
  if (waiting().length === 0) return
  // The pass that just landed is pending work until the durable boundary has
  // committed it. Nothing queued is judged, marked or begun before then.
  for (let attempt = 1; ; attempt++) {
    try {
      await beforePaidCall()
      break
    } catch {
      const s = useStore.getState()
      // A press took the lock while this waited — the worked example's unfurl
      // stopped by a move does exactly this. That pass's landing brings the
      // line back here, to a boundary that includes it.
      if (s.busy) return
      // The boundary also refuses while newer work is still queued behind the
      // write it awaited (the demo's remint, an edit made during the wait).
      // That is a later boundary, not a failure: ask again, a bounded number of
      // times. A save that FAILED is the one thing no later pass can fix, and
      // the presses go back to the reader rather than into a transport that
      // will refuse them.
      if (s.saveState !== 'failed' && attempt < DURABLE_ASKS) continue
      for (const item of waiting())
        s.holdPress(
          item.id,
          'This browser has not saved the last pass, so this one waits for you. Press it to try.',
          item.est,
        )
      return
    }
  }
  // Everything from here to the dispatch is synchronous, so no other press can
  // slip between the judgement and the lock the verb takes.
  for (;;) {
    const s = useStore.getState()
    const item = waiting()[0]
    if (!item) return
    const verdict = upNextVerdict(s, item, {
      est: estimateOf(s, item.verb),
      callable: resolveFor(s, upNextRole(item.verb)) !== null,
      // The pass that landed last is the one in front: the newest op to finish.
      failedBefore: s.session.ops.findLast((op) => op.finishedAt !== undefined)?.errorKind,
    })
    if (verdict.kind === 'wait') return
    if (verdict.kind === 'drop') {
      s.unqueuePress(item.id)
      s.toast(verdict.reason, 'warn')
      continue
    }
    if (verdict.kind === 'hold') {
      s.holdPress(item.id, verdict.reason, verdict.est)
      continue
    }
    // Off the line BEFORE it runs: one press, one pass, whatever the verb does.
    s.unqueuePress(item.id)
    fire(item, item.nodeId)
    // It took the lock; the next one waits for this one to land. A verb that
    // refused (and said why) took nothing, and the next may run now.
    if (useStore.getState().busy) return
  }
}

let armed = false

/**
 * Wire the line to the store, once. See the header for why this is an explicit
 * call from App's boot.
 */
export function armUpNext(): void {
  if (armed) return
  armed = true
  // The one rename that is not a switch: a paid pass on the worked example
  // remints its id (`beginOp`, `core/rename.ts`). The line follows it — the
  // document on screen did not change.
  onRenamed(({ from, to }) => {
    const { upNext } = useStore.getState()
    if (upNext.some((i) => i.sessionId === from))
      useStore.setState({
        upNext: upNext.map((i) => (i.sessionId === from ? { ...i, sessionId: to } : i)),
      })
  })
  useStore.subscribe((state, before) => {
    // Nothing queues across explorations. A microtask late, because the store
    // announces a rename just AFTER the write that changed the id, and a press
    // made on the demo must survive it becoming the reader's own.
    if (state.session.id !== before.session.id && state.upNext.length) {
      queueMicrotask(() => {
        const s = useStore.getState()
        const kept = s.upNext.filter((i) => i.sessionId === s.session.id)
        if (kept.length !== s.upNext.length) useStore.setState({ upNext: kept })
      })
    }
    // The lock fell: whatever is waiting gets its turn.
    if (before.busy && !state.busy && state.upNext.some((i) => !i.held)) void drainUpNext()
  })
}
